import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocketContext } from '../contexts/SocketContext';
import { completedDisplayFrame, DisplayFrame } from '../utils/customerDisplay';

export interface CustomerDisplayInfo { id: string; name: string; port: string; conflict: boolean; }
type Reply = { ok: boolean; error?: string; displays?: CustomerDisplayInfo[] };

/**
 * `enabled` = ο διακόπτης του καταστήματος (store.customer_display_enabled === true, migration 249).
 * Κλειστός: καμία λίστα, καμία εγγραφή, κανένα release — τίποτα δεν στέλνεται, ακόμη κι αν ο
 * browser θυμάται επιλεγμένη οθόνη. Ο server έχει τη δική του πύλη (services/customerDisplays.js).
 */
export function useCustomerDisplay(storeId: number | undefined, frame: DisplayFrame, activeSale: boolean, enabled: boolean) {
  const { socket, isConnected } = useSocketContext();
  const key = `xentrix_customer_display_${storeId}`;
  const [selection, setSelection] = useState({ key, id: localStorage.getItem(key) || '' });
  const selectedId = selection.key === key ? selection.id : localStorage.getItem(key) || '';
  const [displays, setDisplays] = useState<CustomerDisplayInfo[]>([]);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [override, setOverride] = useState<DisplayFrame | null>(null);
  const overrideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameKey = JSON.stringify(frame);
  const completedAtFrame = useRef('');
  const latest = useRef(frame);
  latest.current = override || frame;

  const select = (id: string) => {
    localStorage.setItem(key, id);
    setSelection({ key, id });
  };

  const refresh = useCallback(() => {
    if (!enabled || !socket?.connected) return;
    socket.timeout(7000).emit('customer-display-list', (err: Error | null, reply: Reply) => {
      if (!err && reply?.ok) setDisplays(reply.displays || []);
      else setError(reply?.error || 'Δεν είναι διαθέσιμη η λίστα οθονών');
    });
  }, [socket, enabled]);

  useEffect(() => { setDisplays([]); if (isConnected) refresh(); }, [refresh, isConnected, storeId]);
  useEffect(() => () => { if (overrideTimer.current) clearTimeout(overrideTimer.current); }, []);
  useEffect(() => {
    setOverride(null);
    if (overrideTimer.current) clearTimeout(overrideTimer.current);
  }, [storeId]);
  useEffect(() => {
    if (activeSale && completedAtFrame.current !== frameKey) setOverride(null);
  }, [activeSale, frameKey]);

  const complete = (total: number, cashReceived?: number) => {
    if (!enabled) return;
    if (overrideTimer.current) clearTimeout(overrideTimer.current);
    completedAtFrame.current = frameKey;
    setOverride(completedDisplayFrame(total, cashReceived));
  };
  // Τα 8″ του «ΕΥΧΑΡΙΣΤΟΥΜΕ!» μετρούν από τη στιγμή που αδειάζει το καλάθι, όχι από την πληρωμή.
  // Στην κάρτα POS το complete() γίνεται με την επιτυχή χρέωση και το καλάθι αδειάζει μετά την
  // απόδειξη (Oxygen): με χρονόμετρο από την πληρωμή, μια αργή απόδειξη ξανάδειχνε στον πελάτη
  // «ΣΥΝΟΛΟ / ΠΡΟΣ ΠΛΗΡΩΜΗ» για ήδη πληρωμένο καλάθι.
  useEffect(() => {
    if (!override || activeSale) return undefined;
    overrideTimer.current = setTimeout(() => setOverride(null), 8000);
    return () => { if (overrideTimer.current) clearTimeout(overrideTimer.current); };
  }, [override, activeSale]);

  // One in-flight write, coalescing rapid basket changes. Offline frames are
  // never buffered by Socket.IO; reconnect sends only the current sale.
  const scheduleRef = useRef<() => void>(() => {});
  useEffect(() => {
    setConnected(false);
    setError('');
    if (!enabled || !selectedId || !socket || !isConnected) return;
    let disposed = false;
    let busy = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let lastFrame = '';
    let lastSuccess = 0;
    const send = () => {
      if (disposed || busy || !socket.connected) return;
      const current = JSON.stringify(latest.current);
      if (current === lastFrame && Date.now() - lastSuccess < 10000) return;
      busy = true;
      socket.timeout(7500).emit('customer-display-write', { displayId: selectedId, lines: latest.current }, (err: Error | null, reply: Reply) => {
        busy = false;
        if (disposed) return;
        const ok = !err && reply?.ok === true;
        setConnected(ok);
        setError(ok ? '' : reply?.error || 'Η οθόνη δεν απάντησε');
        if (ok) { lastFrame = current; lastSuccess = Date.now(); }
        if (ok && current !== JSON.stringify(latest.current)) schedule();
      });
    };
    const schedule = () => { clearTimeout(timer); timer = setTimeout(send, 150); };
    scheduleRef.current = schedule;
    schedule();
    const heartbeat = setInterval(send, 2000);
    return () => {
      disposed = true;
      clearTimeout(timer);
      clearInterval(heartbeat);
      scheduleRef.current = () => {};
      if (socket.connected) socket.emit('customer-display-release');
    };
  }, [socket, isConnected, selectedId, storeId, enabled]);
  useEffect(() => { scheduleRef.current(); }, [frameKey, override]);

  return { enabled, selectedId, select, displays, refresh, connected: connected && isConnected, error, complete };
}
